import React, { Component } from 'react';
import { Alert, Avatar, Breadcrumb, Button, Card, Empty, Table, Tag } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';

import AntIcon from '../shared/components/AntIcon';
import ImgLink from './components/imgLink';
import PostDetailModal from './components/PostDetailModal';
import { getApiErrorMessage, listAdminPosts } from './api';

const DESKTOP_IMAGE_STYLE = {
  objectFit: 'cover',
  height: 72,
  width: 96,
  maxWidth: '100%',
  display: 'block',
  borderRadius: 4,
};

const MOBILE_IMAGE_STYLE = {
  objectFit: 'cover',
  height: 88,
  width: 88,
  maxWidth: '100%',
  display: 'block',
  borderRadius: 6,
};

const MOBILE_BREAKPOINT = 768;

function renderTags(tags) {
  if (!Array.isArray(tags) || tags.length === 0) {
    return <span className="posts-list__muted">No tags</span>;
  }
  return (
    <div className="posts-list__tags">
      {tags.map((tag, index) => (
        <Tag color="geekblue" key={typeof tag === 'string' ? tag : index}>
          {typeof tag === 'string' ? tag : (tag && tag.title) || '—'}
        </Tag>
      ))}
    </div>
  );
}

class List extends Component {
  state = {
    posts: [],
    error: '',
    loading: true,
    isMobile: typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
    selectedPost: null,
    detailOpen: false,
    pagination: {
      page: 1,
      limit: 20,
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };

  componentDidMount() {
    this.loadPosts();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile });
    }
  }

  loadPosts = ({ page = 1, limit = this.state.pagination.limit } = {}) => {
    this.setState({ loading: true, error: '' });
    return listAdminPosts({ page, limit })
      .then((response) => {
        const posts = Array.isArray(response.data) ? response.data : [];
        this.setState({
          posts: posts.filter((p) => p && !p.isDeleted),
          pagination: response.pagination || {
            page,
            limit,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
          },
        });
      })
      .catch((error) => {
        this.setState({
          error: getApiErrorMessage(error, 'Failed to load posts.'),
          posts: [],
        });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  openPostDetail = (post) => {
    if (!post) return;
    this.setState({ selectedPost: post, detailOpen: true });
  }

  closePostDetail = () => {
    this.setState({ detailOpen: false, selectedPost: null });
  }

  getColumns = () => [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      width: 110,
      render: (createdAt) => (createdAt ? moment.utc(createdAt).format('ll') : '-'),
    },
    {
      title: 'Image',
      key: 'thumbnail',
      dataIndex: 'image',
      width: 112,
      render: (image) => (
        <span className="posts-list__thumb-link">
          <ImgLink imageStyle={DESKTOP_IMAGE_STYLE} link={image} />
        </span>
      ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      width: 140,
      ellipsis: true,
      render: (author) => (author && author.username) || '—',
    },
    {
      title: 'Content',
      dataIndex: 'content',
      width: 200,
      ellipsis: true,
      render: (content) => content || <span className="posts-list__muted">—</span>,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      width: 180,
      render: renderTags,
    },
    {
      title: 'Likes',
      dataIndex: 'totalNumberOfLikes',
      width: 72,
      align: 'center',
      render: (value) => (value == null ? 0 : value),
    },
    {
      title: 'Comments',
      dataIndex: 'totalNumberOfComments',
      width: 96,
      align: 'center',
      render: (value) => (value == null ? 0 : value),
    },
  ];

  renderMobileCards() {
    const { posts, loading } = this.state;

    if (loading) {
      return <Card loading style={{ marginBottom: 12 }} />;
    }

    if (!posts.length) {
      return <Empty description="No posts found" />;
    }

    return (
      <div className="posts-list__cards">
        {posts.map((post) => {
          const author = post.author || {};
          return (
            <Card
              key={post._id}
              size="small"
              className="posts-list__card posts-list__card--clickable"
              onClick={() => this.openPostDetail(post)}
              title={(
                <div className="posts-list__card-title">
                  <Avatar src={author.image} size={28}>
                    {(author.username || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <span className="posts-list__card-author">{author.username || '—'}</span>
                </div>
              )}
              extra={(
                <Link
                  to={`/posts/${post._id}/edit`}
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit
                </Link>
              )}
            >
              <div className="posts-list__card-body">
                <div className="posts-list__card-image">
                  <ImgLink imageStyle={MOBILE_IMAGE_STYLE} link={post.image} />
                </div>
                <div className="posts-list__card-meta">
                  <div className="posts-list__card-date">
                    {post.createdAt ? moment.utc(post.createdAt).format('ll') : '-'}
                  </div>
                  <div className="posts-list__card-content">
                    {post.content || <span className="posts-list__muted">No content</span>}
                  </div>
                  {renderTags(post.tags)}
                  <div className="posts-list__card-stats">
                    <span><AntIcon type="heart" /> {post.totalNumberOfLikes == null ? 0 : post.totalNumberOfLikes}</span>
                    <span><AntIcon type="message" /> {post.totalNumberOfComments == null ? 0 : post.totalNumberOfComments}</span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  renderPaginationControls() {
    const { pagination, loading } = this.state;
    const { page, limit, totalCount, totalPages, hasNextPage, hasPrevPage } = pagination;

    return (
      <div className="posts-list__pagination">
        <span className="posts-list__pagination-total">
          Total {totalCount} posts
        </span>
        <Button.Group size="small">
          <Button
            disabled={loading || !hasPrevPage || page <= 1}
            onClick={() => this.loadPosts({ page: page - 1, limit })}
          >
            Prev
          </Button>
          <Button disabled>
            {page} / {totalPages || 1}
          </Button>
          <Button
            disabled={loading || !hasNextPage}
            onClick={() => this.loadPosts({ page: page + 1, limit })}
          >
            Next
          </Button>
        </Button.Group>
      </div>
    );
  }

  render() {
    const { posts, error, loading, isMobile, pagination, selectedPost, detailOpen } = this.state;

    return (
      <div id="posts-list">
        <Breadcrumb>
          <Breadcrumb.Item>Posts</Breadcrumb.Item>
        </Breadcrumb>

        <div className="container">
          <h4>
            Manage Posts
            <Button.Group size="small" className="is-right">
              <Button onClick={() => this.loadPosts({ page: pagination.page })}>
                <AntIcon type="reload" /> Refresh
              </Button>
              <Button>
                <Link to="/posts/new"><AntIcon type="plus" /> New Post</Link>
              </Button>
            </Button.Group>
          </h4>

          {error && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={error}
            />
          )}

          {isMobile ? (
            <>
              {this.renderMobileCards()}
              {this.renderPaginationControls()}
            </>
          ) : (
            <div className="posts-list__table-wrap">
              <Table
                scroll={{ x: 910 }}
                loading={loading}
                columns={this.getColumns()}
                dataSource={posts}
                rowKey="_id"
                tableLayout="fixed"
                onRow={(record) => ({
                  onClick: () => this.openPostDetail(record),
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: pagination.totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '30', '50'],
                  onChange: (page, pageSize) => this.loadPosts({ page, limit: pageSize }),
                  onShowSizeChange: (page, pageSize) => this.loadPosts({ page: 1, limit: pageSize }),
                  showTotal: (total) => `Total ${total} posts`,
                }}
              />
            </div>
          )}
        </div>

        <PostDetailModal
          post={selectedPost}
          open={detailOpen}
          onClose={this.closePostDetail}
        />
      </div>
    );
  }
}

export default List;
